import { EoWriter, PacketAction, type PacketFamily } from 'eolib';
import type { Client } from '@/client';
import { GameState } from '@/game-state';

export type FlairSlot = 'armor' | 'hat' | 'boots';
export type FlairChoice = { item: number; slot: FlairSlot; name: string; graphic: number };
export type FlairSnapshot = {
  kind: 'flair'; map_id: number; character_id: number; player_id: number;
  selected: Record<FlairSlot, number>; choices: FlairChoice[]; error?: string | null;
};
type Store = {
  snapshot?: FlairSnapshot; pending: boolean; error: string;
  character: number; map: number; listeners: Set<() => void>; timer?: number;
};
const stores = new WeakMap<Client, Store>();
const FAMILY_FLAIR = 58 as PacketFamily;
const slots: FlairSlot[] = ['armor', 'hat', 'boots'];
const positive = (v: unknown): v is number => Number.isSafeInteger(v) && (v as number) > 0;

function notify(s: Store): void { for (const f of s.listeners) f(); }
function store(c: Client): Store {
  let s = stores.get(c);
  if (!s) {
    s = { pending: false, error: '', character: c.characterId, map: c.mapId, listeners: new Set() };
    stores.set(c, s);
    c.subscribeStateChanged(state => {
      if (state !== GameState.InGame) {
        const value = stores.get(c)!;
        clearTimeout(value.timer); value.snapshot = undefined; value.pending = false; value.error = '';
        notify(value);
      }
    });
  }
  if (s.character !== c.characterId || s.map !== c.mapId) {
    clearTimeout(s.timer); s.snapshot = undefined; s.pending = false; s.error = '';
    s.character = c.characterId; s.map = c.mapId;
  }
  return s;
}

export function getFlair(c: Client) {
  const s = store(c); return { snapshot: s.snapshot, pending: s.pending, error: s.error };
}
export function subscribeFlair(c: Client, f: () => void): () => void {
  const s = store(c); s.listeners.add(f); return () => { s.listeners.delete(f); };
}
export function receiveFlair(c: Client, data: unknown): void {
  if (!data || typeof data !== 'object') return;
  const v = data as FlairSnapshot;
  if (v.kind !== 'flair' || v.map_id !== c.mapId || v.character_id !== c.characterId || v.player_id !== c.playerId
    || !v.selected || !slots.every(slot => Number.isSafeInteger(v.selected[slot]) && v.selected[slot] >= 0)
    || !Array.isArray(v.choices) || !v.choices.every(i => i && positive(i.item) && slots.includes(i.slot)
      && typeof i.name === 'string' && Number.isSafeInteger(i.graphic) && i.graphic >= 0)
    || (v.error != null && typeof v.error !== 'string')) return;
  const s = store(c); clearTimeout(s.timer); s.pending = false; s.snapshot = v; s.error = v.error ?? ''; notify(s);
}

export function sendFlair(c: Client, action: 'flair_view' | 'flair_equip' | 'flair_unequip', slot?: FlairSlot, item?: number): boolean {
  const s = store(c);
  if (c.state !== GameState.InGame || s.pending) return false;
  if (action !== 'flair_view' && (!slot || !slots.includes(slot))) return false;
  if (action === 'flair_equip' && !positive(item)) return false;
  const payload = action === 'flair_view' ? {} : action === 'flair_equip' ? { slot, item } : { slot };
  const writer = new EoWriter(); writer.addString(JSON.stringify({ action, node: JSON.stringify(payload) }));
  s.pending = true; s.error = ''; clearTimeout(s.timer);
  s.timer = window.setTimeout(() => {
    s.pending = false; s.error = 'No reply yet. Refresh to check your style.'; notify(s);
  }, 6000);
  try { c.bus.sendBuf(FAMILY_FLAIR, PacketAction.Request, writer.toByteArray()); notify(s); return true; }
  catch { clearTimeout(s.timer); s.pending = false; s.error = 'Connection unavailable. Please reconnect.'; notify(s); return false; }
}
